// leave-request.component.ts
// REMOVED: Inline cell renderer HTML strings (→ utils/grid-cell-renderers.ts)
// Uses shared formatDateDMY from utils/date.utils.ts

import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { ColDef, ColGroupDef, GridOptions, ICellRendererParams } from 'ag-grid-enterprise';
import { of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { ersConstants, RoutedKeys, dateFilterParamsDMY } from '@rcs/utils';
import { ManagerNotificationPanelComponent, ManagerUpdateNotification } from '@rcs/ers-elementals';

import { ErsGridComponent } from '@rcs/grid';
import { DeleteDialogComponent } from '../../dialogs/delete-dialog/delete-dialog.component';
import { LeaveRequest, LeaveRequestRowData, LeaveBalance, GiftedLeaveBalance, LeaveType, PolicyDocument } from '../../models/leave.model';
import { formatDateDMY, formatDateLong, parseLocalDate } from '../../utils/date.utils';
import { cancelButtonWithStatusRenderer, carryForwardWithInfoRenderer, leaveTypeRenderer, requestButtonRenderer, statusBadgeRenderer } from '../../utils/grid-cell-renderers';
import { LeaveDataService } from '../../services/leave-data.service';
import { LeaveService } from '../../services/leave.service';
import { HourlyPermissionDialogComponent } from '../../dialogs/hourly-permission-dialog/hourly-permission-dialog.component';
import { EmployeeLeaveBalanceComponent } from './employee-leave-balance/employee-leave-balance.component';
import { EmployeeLeaveRequestComponent } from './employee-leave-request/employee-leave-request.component';
import { HolidaysTabComponent } from './holidays-tab/holidays-tab.component';
import { LeavePoliciesComponent } from './leave-policies/leave-policies.component';
import { LmHttpAdapter } from '../../services/lm-http-adapter';
import { LmLoadingOverlayComponent } from '../lm-loading-overlay/lm-loading-overlay.component';
import { ManagerUpdatesService } from '../../services/manager-updates.service';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'ers-leave-request',
  standalone: true,
  imports: [
    TranslocoPipe,
    CommonModule, MatTabsModule, MatButtonModule, MatIconModule,
    MatCardModule, MatTooltipModule, ErsGridComponent,
    EmployeeLeaveRequestComponent, EmployeeLeaveBalanceComponent,
    HolidaysTabComponent, LeavePoliciesComponent,
    ManagerNotificationPanelComponent,
    LmLoadingOverlayComponent,
  ],
  templateUrl: './leave-request.component.html',
  styleUrls: ['./leave-request.component.scss'],
})
export class LeaveRequestComponent implements OnInit {
  // View state
  selectedView: 'myLeaves' | 'employeeRequests' = 'myLeaves';
  selectedMainTab = 0;
  selectedSubTab = 0;
  selectedEmployeeTab = 0;
  private tab0Loaded = false;

  /** Updates from Manager panel */
  updatesOpen = false;
  showNotificationPanel = false;
  managerUpdates: ManagerUpdateNotification[] = [];
  notificationLoading = false;

  readonly isEmployee = true;

  /** True when the logged-in user has the MANAGER role */
  isManager = false;
  hasConfigurationsAccess = false;

  private readonly _adapter = inject(LmHttpAdapter);

  // ── RBA visibility (driven by the backend's role-based-auth map) ──────────
  /** TODO: set to false once DB route key config is in place */
  private readonly bypassLmAuth = true;
  /** Returns true if the current user has access to the given routedKey. */
  private rba(key: string): boolean {
    if (this.bypassLmAuth) return true;
    const map = JSON.parse(
      localStorage.getItem(ersConstants.ROLE_BASED_AUTHENTICATION) ?? '{}'
    );
    return map[key] === true;
  }

  private getRoleAuthMap(): Record<string, boolean> {
    return JSON.parse(
      localStorage.getItem(ersConstants.ROLE_BASED_AUTHENTICATION) ?? '{}'
    );
  }

  get canViewHolidays(): boolean           { return this.rba(RoutedKeys.leaveManagement.leave); }
  get canViewLeavePolicies(): boolean      { return this.rba(RoutedKeys.leaveManagement.leave); }
  get canViewEmployeeRequests(): boolean   { return this.isManager || this.hasConfigurationsAccess; }
  get canViewEmployeeBalanceTab(): boolean { return this.isManager || this.hasConfigurationsAccess; }
  get canViewRequestLeave(): boolean       { return this.rba(RoutedKeys.leaveManagement.leave); }
  //get canViewGiftOfTime(): boolean         { return this.rba(RoutedKeys.leaveManagement.leave); }
  get canViewGiftOfTime(): boolean         { return false; }
  //get canViewAvailGiftOfTime(): boolean    { return this.rba(RoutedKeys.leaveManagement.leave); }
  get canViewAvailGiftOfTime(): boolean    { return false; }

  // Gift of Time balance
  giftedLeaveBalance: GiftedLeaveBalance = { totalGifted: 0, totalUsed: 0, balanceRemaining: 0, expiresOn: null };

  // Data
  leaveRequests:   LeaveRequest[]   = [];
  leaveBalances:   LeaveBalance[]   = [];
  leaveTypes:      LeaveType[]      = [];
  policyDocuments: PolicyDocument[] = [];
  displayData:     LeaveRequestRowData[] = [];
  hourlyDisplayData: any[] = [];
  isLoadingRequests = true;
  isLoadingBalance  = true;
  isCancelling = false;

  // Grid
  gridOptions: Partial<GridOptions> = {};
  balanceGridOptions: Partial<GridOptions> = {};
  columnDefs: (ColDef | ColGroupDef)[] = [];
  hourlyColumnDefs: (ColDef | ColGroupDef)[] = [];
  balanceColumnDefs: ColDef[] = [];
  balanceRowData: any[] = [];
  gridHeight = 'calc(100vh - 260px - var(--ers-tab-strip-height, 0px))';

  private destroyRef = inject(DestroyRef);
  private rolesResolved = false;
  private _pendingView: string | null = null;

  constructor(
    private leaveDataService: LeaveDataService,
    private leaveService: LeaveService,
    private managerUpdatesService: ManagerUpdatesService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    // configurations access is available synchronously from localStorage
    this.hasConfigurationsAccess = this.getRoleAuthMap()[RoutedKeys.leaveManagement.configurations] === true;

    // isManager requires an API call — resolve async, then guard the route
    this._adapter.selectedUserTenant$.pipe(take(1)).subscribe(tenant => {
      const supervisorEmployeeId = String(tenant?.['employeeId'] ?? '').trim();
      if (!supervisorEmployeeId) {
        this.isManager = false;
        this.managerUpdatesService.loadNotifications();
        this.rolesResolved = true;
        if (this._pendingView) {
          this.selectedView = this._pendingView as 'myLeaves' | 'employeeRequests';
          this._pendingView = null;
          this.guardEmployeeRequestsRoute();
        }
        this.guardEmployeeRequestsRoute();
        return;
      }

      this.leaveService.getEmployeesBySupervisorEmployeeId(supervisorEmployeeId)
        .pipe(take(1))
        .subscribe((rows: any[]) => {
          this.isManager = Array.isArray(rows) && rows.length > 0;
          this.managerUpdatesService.loadNotifications();
          this.rolesResolved = true;
          // Apply any pending view that was waiting for role resolution.
          if (this._pendingView) {
            this.selectedView = this._pendingView as 'myLeaves' | 'employeeRequests';
            this._pendingView = null;
            this.guardEmployeeRequestsRoute();
          }
          this.guardEmployeeRequestsRoute();
        });
    });

    this.setupGrid();
    this.subscribeToData();

    this.route.data.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      const tab  = data['tab']  ?? 0;
      const view = data['view'] ?? 'myLeaves';
      const employeeTab = data['employeeTab'] ?? 0;
      this.selectedMainTab = tab;
      this.selectedEmployeeTab = employeeTab;
      if (view === 'employeeRequests' && !this.rolesResolved && !this.hasConfigurationsAccess) {
        this._pendingView = view;
      } else {
        this.selectedView = view;
      }

      // Redirect only after roles are resolved to avoid false redirects while isManager is still in-flight.
      // Config-access users (hasConfigurationsAccess) are covered synchronously.
      // Supervisor-only users are handled by guardEmployeeRequestsRoute() once the API resolves.
      if (view === 'employeeRequests' && this.rolesResolved && !this.canViewEmployeeRequests) {
        this.router.navigate([LeaveRequestComponent.VIEW_ROUTES['myLeaves']]);
        return;
      }

      if (view === 'myLeaves' && tab === 0 && !this.tab0Loaded) {
        this.loadData();
        this.tab0Loaded = true;
      }
    });
  }

  private static readonly VIEW_ROUTES: Record<string, string> = {
    myLeaves:         '/ers/leave-management/leave',
    employeeRequests: '/ers/leave-management/employee-leave-requests',
  };

  private static readonly EMPLOYEE_TAB_ROUTES = [
    '/ers/leave-management/employee-leave-requests',
    '/ers/leave-management/employee-leave-balance',
  ];

  private static readonly TAB_ROUTES = [
    '/ers/leave-management/leave',
    '/ers/leave-management/holidays',
    '/ers/leave-management/leave-policies',
  ];

  selectMainTab(tab: number): void {
    if (tab === this.selectedMainTab) return;
    this.router.navigate([LeaveRequestComponent.TAB_ROUTES[tab]]);
  }

  /** Called after isManager resolves; redirects away from employeeRequests if the user lacks access. */
  private guardEmployeeRequestsRoute(): void {
    if (this.selectedView === 'employeeRequests' && !this.canViewEmployeeRequests) {
      this.router.navigate([LeaveRequestComponent.VIEW_ROUTES['myLeaves']]);
      return;
    }
  }

  switchView(view: 'myLeaves' | 'employeeRequests'): void {
    if (view === 'employeeRequests') {
      this.router.navigate([LeaveRequestComponent.EMPLOYEE_TAB_ROUTES[this.selectedEmployeeTab] ?? LeaveRequestComponent.EMPLOYEE_TAB_ROUTES[0]]);
      return;
    }

    this.router.navigate([LeaveRequestComponent.VIEW_ROUTES['myLeaves']]);
  }

  selectEmployeeTab(tab: number): void {
    if (tab === this.selectedEmployeeTab) return;
    this.router.navigate([LeaveRequestComponent.EMPLOYEE_TAB_ROUTES[tab] ?? LeaveRequestComponent.EMPLOYEE_TAB_ROUTES[0]]);
  }

  onEmployeeSectionChange(section: 'requests' | 'balance'): void {
    const nextTab = section === 'balance' ? 1 : 0;
    if (this.selectedEmployeeTab === nextTab) return;
    this.selectedEmployeeTab = nextTab;
  }

  toggleNotificationPanel(): void {
    this.showNotificationPanel = !this.showNotificationPanel;
    this.updatesOpen = this.showNotificationPanel;
  }

  setupGrid(): void {
    this.columnDefs = [
      { headerName: 'Date',       field: 'date',      width: 120, filter: 'agDateColumnFilter', filterParams: dateFilterParamsDMY },
      { headerName: 'Leave Type', field: 'leaveType', width: 150, filter: 'agSetColumnFilter' },
      {
        headerName: 'Days Requested',
        marryChildren: true,
        children: [
          { headerName: 'From',       field: 'fromDate',   width: 120, suppressHeaderMenuButton: true, filter: 'agDateColumnFilter', filterParams: dateFilterParamsDMY },
          { headerName: 'To',         field: 'toDate',     width: 120, suppressHeaderMenuButton: true, filter: 'agDateColumnFilter', filterParams: dateFilterParamsDMY },
          { headerName: 'Total Days', field: 'totalDays',  width: 100, cellStyle: { textAlign: 'center' }, suppressHeaderMenuButton: true },
        ],
      },
      { headerName: 'Request To', field: 'requestTo', width: 150, filter: 'agSetColumnFilter' },
      { headerName: 'Status',     field: 'status',    width: 130, filter: 'agSetColumnFilter', cellRenderer: statusBadgeRenderer },
      { headerName: 'Action',     field: 'action',    width: 150, cellRenderer: cancelButtonWithStatusRenderer, suppressHeaderMenuButton: true },
      // Hidden sort column — sorts grid by modifiedOn descending (most recent on top)
      { field: '_modifiedOn', hide: true, sort: 'desc', sortIndex: 0 },
    ];

    this.hourlyColumnDefs = [
      { headerName: 'Date',            field: 'date',           width: 120, filter: 'agDateColumnFilter', filterParams: dateFilterParamsDMY },
      { headerName: 'Permission Type', field: 'permissionType', width: 160, filter: 'agSetColumnFilter' },
      {
        headerName: 'Duration Requested',
        marryChildren: true,
        children: [
          { headerName: 'From',        field: 'fromTime',   width: 120, suppressHeaderMenuButton: true, filter: 'agSetColumnFilter' },
          { headerName: 'To',          field: 'toTime',     width: 120, suppressHeaderMenuButton: true, filter: 'agSetColumnFilter' },
          { headerName: 'Total Hours', field: 'totalHours', width: 110, cellStyle: { textAlign: 'center' }, suppressHeaderMenuButton: true },
        ],
      },
      { headerName: 'Request To', field: 'requestTo', width: 150, filter: 'agSetColumnFilter' },
      { headerName: 'Status',     field: 'status',    width: 130, filter: 'agSetColumnFilter', cellRenderer: statusBadgeRenderer },
      { headerName: 'Action',     field: 'action',    width: 150, cellRenderer: cancelButtonWithStatusRenderer, suppressHeaderMenuButton: true },
      // Hidden sort column — sorts grid by modifiedOn descending (most recent on top)
      { field: '_modifiedOn', hide: true, sort: 'desc', sortIndex: 0 },
    ];

    const daysFormatter = (p: ICellRendererParams) =>
      p.value != null ? `${p.value} Days` : '-';

    const validTillFormatter = (p: ICellRendererParams) =>
      p.value
        ? formatDateLong(p.value)
        : '-';

    this.balanceColumnDefs = [
      { headerName: 'Leave Type',        field: 'leaveTypeName',  width: 200, filter: 'agSetColumnFilter', cellRenderer: leaveTypeRenderer },
      { headerName: 'Total Allocated',   field: 'totalAllocated', width: 140, cellRenderer: daysFormatter },
      { headerName: 'Total Used',        field: 'used',           width: 120, cellRenderer: daysFormatter },
      { headerName: 'Reserved',          field: 'reservedLeaves', width: 120, cellRenderer: daysFormatter },
      { headerName: 'Remaining Balance', field: 'remaining',      width: 160, cellRenderer: daysFormatter },
      { headerName: 'Carried Forward',    field: 'carryForward',   width: 140, cellRenderer: carryForwardWithInfoRenderer },
      { headerName: 'Unpaid Leaves',     field: 'unpaidLeaves',   width: 120, filter: 'agSetColumnFilter' },
      { headerName: 'Valid Till',        field: 'validTill',      width: 140, filter: 'agSetColumnFilter', cellRenderer: validTillFormatter },
      { headerName: '',                  field: 'action',         width: 160, cellRenderer: requestButtonRenderer },
    ];

    this.gridOptions = {
      suppressCellFocus: false,
      enableCellTextSelection: true,
      groupHeaderHeight: 22,
      paginationAutoPageSize: true,
      // Columns are all fixed-width (no flex), so on wide screens they leave
      // a dead gap after the last column; stretch them to fill it instead.
      onGridReady: (params: any) => params.api.sizeColumnsToFit(),
      onGridSizeChanged: (params: any) => params.api.sizeColumnsToFit(),
    };

    this.balanceGridOptions = {
      suppressCellFocus: true,
      onGridReady: (params: any) => params.api.sizeColumnsToFit(),
      onGridSizeChanged: (params: any) => params.api.sizeColumnsToFit(),
    };
  }

  loadData(): void {
    this.isLoadingRequests = true;
    this.isLoadingBalance  = true;

    this.leaveService.getMyLeaveRequests().subscribe(requests => {
      this.leaveRequests = requests;
      this.refreshLeaveGrid();
      this.isLoadingRequests = false;
    });

    this.leaveService.getMyLeaveBalance().subscribe(balances => {
      this.leaveBalances = balances;
      this.buildBalanceRowData();
      this.isLoadingBalance = false;
    });

    this.leaveService.getMyGiftedLeaveBalance()
      .pipe(catchError(() => of({ totalGifted: 0, totalUsed: 0, balanceRemaining: 0, expiresOn: null })))
      .subscribe(gb => {
        this.giftedLeaveBalance = gb;
      });

    // Pre-load leave types and policy docs so dialog opens instantly
    this.leaveService.getLeaveTypes()
      .pipe(catchError(() => of([])))
      .subscribe(types => { this.leaveTypes = types; this.buildBalanceRowData(); });

    this.leaveService.getPolicyDocuments()
      .pipe(catchError(() => of([])))
      .subscribe((docs: any[]) => { this.policyDocuments = docs as PolicyDocument[]; });
  }

  subscribeToData(): void {
    this.leaveDataService.myLeaveRequests$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(requests => {
        this.leaveRequests = requests;
        this.refreshLeaveGrid();
      });

    this.leaveDataService.leaveBalances$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(balances => {
        this.leaveBalances = balances;
      });

    this.managerUpdatesService.notifications$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((notifications: ManagerUpdateNotification[]) => {
        this.managerUpdates = notifications;
      });

    this.managerUpdatesService.loading$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading: boolean) => {
        this.notificationLoading = loading;
      });
  }

  private buildBalanceRowData(): void {
    if (!this.leaveBalances?.length) return;
    const typeMap = new Map<string, any>();
    (this.leaveTypes ?? []).forEach((t: any) => typeMap.set(t.id, t));

    this.balanceRowData = this.leaveBalances.map(b => {
      const lt = typeMap.get(b.leaveTypeId);
      return {
        leaveTypeId:      b.leaveTypeId,
        leaveTypeName:    b.leaveTypeName || b.leaveTypeId,
        totalAllocated:   b.totalAllocated,
        used:             b.totalUsed,
        remaining:        b.remainingBalance,
        reservedLeaves:   b.reservedLeaves,
        availableBalance: b.availableBalance,
        carryForward:     b.carriedForwardLeaves ?? 0,
        isCarriedForward: lt?.isCarriedForward ?? b.isCarriedForward,
        carryForwardDays: lt?.carryForwardDays ?? null,
        unpaidLeaves:     b.unpaidLeaves,
        validTill:        b.validTill ?? null,
        _raw: b,
      };
    });
  }

  refreshLeaveGrid(): void {
    const leaveRows = this.leaveRequests.filter(req => !req.isHourlyLeaveRequest);
    const hourlyRows = this.leaveRequests.filter(req => !!req.isHourlyLeaveRequest);

    this.displayData = leaveRows.map(req => {
      const start = this.parseDate(req.startDate);
      const end = this.parseDate(req.endDate);
      const computedDays = (start && end)
        ? Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1
        : 0;
      return {
        id: req.id,
        // Per requirement, Date column must show createdOn (not leave start date)
        date: this.formatDateOrDash(req.createdOn),
        leaveType: req.leaveTypeName || '',
        fromDate: this.formatDateOrDash(req.startDate),
        toDate: this.formatDateOrDash(req.endDate),
        totalDays: req.totalDays || computedDays,
        requestTo: req.requestToName || '—',
        status: req.status || 'Pending',
        reason: req.reason,
        startDateRaw: start ? `${start.getFullYear()}-${String(start.getMonth()+1).padStart(2,'0')}-${String(start.getDate()).padStart(2,'0')}` : '',        // ISO date for default descending sort by modifiedOn (approvedOn if processed, else createdOn)
        _modifiedOn: (this.parseDate(req.approvedOn ?? req.createdOn) ?? this.parseDate(req.createdOn) ?? new Date(0)).toISOString(),        _raw: req,
      };
    });

    this.hourlyDisplayData = hourlyRows.map(req => {
      const start = this.parseDate(req.startDate);
      const end = this.parseDate(req.endDate);
      const hours = start && end ? Math.max(0, (end.getTime() - start.getTime()) / 3_600_000) : 0;
      const totalHours = hours ? String(Math.floor(hours)).padStart(2, '0') : '00';
      return {
        id: req.id,
        date: this.formatDateOrDash(req.startDate),
        permissionType: req.leaveTypeName || '-',
        fromTime: start
          ? start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
          : '-',
        toTime: end
          ? end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
          : '-',
        totalHours,
        requestTo: req.requestToName || '—',
        status: req.status || 'Pending',
        isHourly: true,
        startDateRaw: start ? `${start.getFullYear()}-${String(start.getMonth()+1).padStart(2,'0')}-${String(start.getDate()).padStart(2,'0')}` : '',
        startIsoRaw: start ? start.toISOString() : '',        // ISO date for default descending sort by modifiedOn
        _modifiedOn: (this.parseDate(req.approvedOn ?? req.createdOn) ?? this.parseDate(req.createdOn) ?? new Date(0)).toISOString(),        _raw: req,
      };
    });
  }

  private parseDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    const d = parseLocalDate(value);
    return isNaN(d.getTime()) ? null : d;
  }

  private formatDateOrDash(value: string | Date | null | undefined): string {
    if (!value) return '-';
    return formatDateDMY(value) || '-';
  }

  onRequestLeave(selectedLeaveTypeId?: string, lockLeaveType = false): void {
    const queryParams: Record<string, string> = {};
    if (selectedLeaveTypeId) queryParams['leaveTypeId'] = selectedLeaveTypeId;
    if (lockLeaveType)       queryParams['lock'] = '1';
    this.router.navigate(['/ers/leave-management/request-leave'], { queryParams });
  }

  onGiftOfTime(): void {
    this.router.navigate(['/ers/leave-management/gift-of-time']);
  }

  onAvailGiftOfTime(): void {
    this.router.navigate(['/ers/leave-management/avail-gift-of-time']);
  }

  onRequestHourlyPermission(): void {
    const ref = this.dialog.open(HourlyPermissionDialogComponent, {
      width: '800px',
      panelClass: 'lm-dialog-panel',
      data: { mode: 'create' },
    });
    ref.afterClosed().subscribe(result => {
      if (result?.success) { this.loadData(); }
    });
  }

  onGridCellClick(event: any): void {
    const mouseEvent = event?.event as MouseEvent | undefined;
    if (!mouseEvent) return;
    const btn = (mouseEvent.target as HTMLElement)?.closest('[data-action="cancel"]') as HTMLElement | null;
    if (!btn || (btn as HTMLButtonElement).disabled) return;
    const row = event.data;
    const id  = row?.id ?? btn.getAttribute('data-id');
    if (!id) return;

    const ref = this.dialog.open(DeleteDialogComponent, {
      width: '480px',
      panelClass: 'lm-dialog-panel',
      data: {
        title: 'Withdraw Leave Request',
        itemName: row?.leaveType ?? '',
        message: 'This action will withdraw your leave request. Are you sure you want to cancel this request?',
        confirmText: 'Yes, Cancel Leave',
        cancelText: 'Keep Leave',
      },
    });

    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.isCancelling = true;
      this.leaveService.cancelLeaveRequest(id).subscribe({
        next: () => { this.isCancelling = false; this.loadData(); },
        error: () => { this.isCancelling = false; },
      });
    });
  }

  onHourlyCellClick(event: any): void {
    const mouseEvent = event?.event as MouseEvent | undefined;
    if (!mouseEvent) return;
    const btn = (mouseEvent.target as HTMLElement)?.closest('[data-action="cancel"]') as HTMLElement | null;
    if (!btn || (btn as HTMLButtonElement).disabled) return;
    const row = event.data;
    const id  = row?.id ?? btn.getAttribute('data-id');
    if (!id) return;

    const ref = this.dialog.open(DeleteDialogComponent, {
      width: '480px',
      panelClass: 'lm-dialog-panel',
      data: {
        title: 'Withdraw Hourly Permission',
        itemName: row?.permissionType ?? '',
        message: 'This action will withdraw your hourly permission request. Are you sure you want to cancel this request?',
        confirmText: 'Yes, Cancel Request',
        cancelText: 'Keep Request',
      },
    });

    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.isCancelling = true;
      this.leaveService.cancelLeaveRequest(id).subscribe({
        next: () => { this.isCancelling = false; this.loadData(); },
        error: () => { this.isCancelling = false; },
      });
    });
  }

  onBalanceCellClick(event: any): void {
    const mouseEvent = event?.event as MouseEvent | undefined;
    if (!mouseEvent) return;
    const btn = (mouseEvent.target as HTMLElement)?.closest('[data-action="request"]') as HTMLElement | null;
    if (!btn) return;
    const leaveTypeId = btn.getAttribute('data-id') ?? '';
    this.onRequestLeave(leaveTypeId, true);
  }

  onRequestLeaveForType(leaveType: string): void {
    const lt = this.leaveTypes.find(t => t.name === leaveType || t.id === leaveType);
    this.onRequestLeave(lt?.id, true);
  }
}